import { BuildingNameType } from '@/enums/building';
import type { storeLangsType } from '@/services/dto';
import type { languageType } from '@/stores';

// Comprehensive language mapping for all supported languages
const LANGUAGE_LABELS_MAP = {
    ko: {
        ko: '한국어',
        en: '영어',
        ja: '일본어',
        'zh-CN': '중국어 간체',
        'zh-TW': '중국어 번체',
    },
    en: {
        ko: 'Korean',
        en: 'English',
        ja: 'Japanese',
        'zh-CN': 'Simplified Chinese',
        'zh-TW': 'Traditional Chinese',
    },
    ja: {
        ko: '韓国語',
        en: '英語',
        ja: '日本語',
        'zh-CN': '中国語（簡体字）',
        'zh-TW': '中国語（繁体字）',
    },
    'zh-CN': {
        ko: '韩语',
        en: '英语',
        ja: '日语',
        'zh-CN': '中文（简体）',
        'zh-TW': '中文（繁体）',
    },
    'zh-TW': {
        ko: '韓語',
        en: '英語',
        ja: '日語',
        'zh-CN': '中文（簡體）',
        'zh-TW': '中文（繁體）',
    },
} as const;

export const AppUtils = {
    /**
     * Get language label based on the active language
     * @param targetLang - The language code to get label for
     * @param activeLang - The currently active language (defaults to 'ko')
     * @returns Language label in the active language
     */
    getLanguageLabelByActiveLang: (targetLang: languageType, activeLang: languageType = 'ko'): string => {
        console.log('getLanguageLabelByActiveLang', LANGUAGE_LABELS_MAP[activeLang][targetLang] || LANGUAGE_LABELS_MAP.ko[targetLang]);
        return LANGUAGE_LABELS_MAP[activeLang][targetLang] || LANGUAGE_LABELS_MAP.ko[targetLang];
    },

    /**
     * Get all language labels for dropdown options based on active language
     * @param langs - Array of available language codes
     * @param activeLang - The currently active language
     * @returns Array of language options with labels in active language
     */
    getLanguageOptionsByActiveLang: (langs: languageType[], activeLang: languageType = 'ko') => {
        const order = ['ko', 'en', 'zh-CN', 'zh-TW', 'ja'] as const;
        return langs
            .sort((a, b) => order.indexOf(a) - order.indexOf(b))
            .map((lang) => ({
                label: AppUtils.getLanguageLabelByActiveLang(lang, activeLang),
                value: lang,
            }));
    },

    getLanguageLabel: (lang: languageType) => {
        const labels: Record<languageType, string> = {
            ko: '한국어',
            en: 'English',
            'zh-CN': '中文(简)',
            'zh-TW': '中文(繁)',
            ja: '日本語',
        };
        return labels[lang];
    },
    getLanguageTTS: (lang: languageType) => {
        const tts: Record<languageType, string> = {
            ko: 'ko-KR',
            en: 'en-US',
            'zh-CN': 'zh-CN',
            'zh-TW': 'zh-TW',
            ja: 'ja-JP',
        };
        return tts[lang];
    },
    getLanguageFont: (lang: languageType, buildingType?: BuildingNameType) => {
        console.log('buildingType', lang, buildingType);
        const fonts: Record<languageType, string> = {
            ko: "'Noto Sans CJK KR', sans-serif",
            en: "'maison', sans-serif",
            'zh-CN': "'Noto Sans CJK SC', sans-serif",
            'zh-TW': "'Noto Sans CJK TC', sans-serif",
            ja: "'Noto Sans CJK JP', sans-serif",
        };
        return fonts[lang];
    },
    OPTION_LANGUAGE: (langs: storeLangsType[]) => {
        return [...langs]
            .sort((a, b) => {
                const orderA = a.sort_order ?? 0;
                const orderB = b.sort_order ?? 0;
                if (orderA !== orderB) {
                    return orderA - orderB;
                }
                return a.code.localeCompare(b.code);
            })
            .map((lang) => ({
                label: lang.display_name || lang.name || AppUtils.getLanguageLabel(lang.code),
                value: lang.code,
            }));
    },
    getPhoneNumberTTSText: (phone: string) => {
        return phone.replaceAll('-', '').split('').join(', ');
    },
    isValidPhoneNumber: (phone?: string): boolean => {
        if (!phone?.trim()) return false;
        const digits = phone.replace(/\D/g, '');
        return digits.length >= 7;
    },
    getFloorTTSText: (floor: string) => {
        return floor.replace(/F/g, '').replace(/f/g, '');
    },
};
